import { Inject, Injectable } from '@angular/core';
import { Router, CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserServiceInterface } from '@hutsix/ngxh6';

@Injectable({
    providedIn: 'root',
})
export class AuthGuardService implements CanActivate {
    constructor(private router: Router, @Inject('UserService') public user: UserServiceInterface) {}

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean {
        return this.user.watch.pipe(
            map(user => {
                if (!user || !user.loggedIn) {
                    this.router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
                    return false;
                }

                if (route.data?.roles?.length && !this.user.isGranted(route.data.roles)) {
                    // Send them to their highest-privilege landing rather than
                    // /unauthorised. Handles the common case of a board /
                    // CSC user being bounced from / after login.
                    const landing = this.bestLandingFor();
                    if (landing !== state.url) {
                        this.router.navigateByUrl(landing);
                    } else {
                        // Already on their landing somehow — surface the
                        // unauthorised page so they're not silently stuck.
                        this.router.navigate(['/unauthorised'], { queryParams: { route: state.url } });
                    }
                    return false;
                }

                return true;
            }),
        );
    }

    /**
     * Picks the best route for the currently-logged-in user based on their
     * highest privilege role. Mirrors the role hierarchy in users-config.ts
     * (Admin > Manager > (CSC | HR) > Staff > User, with Board as a parallel
     * branch). A CSC+HR combo lands on /clients — client care is the primary
     * duty for anyone holding both.
     */
    private bestLandingFor(): string {
        if (this.user.isGranted(['ROLE_MANAGER'])) return '/';                 // dashboard quadrants
        if (this.user.isGranted(['ROLE_CSC'])) return '/clients';              // Quality & Client Care
        if (this.user.isGranted(['ROLE_HR'])) return '/people';                // People / HR
        if (this.user.isGranted(['ROLE_BOARD'])) return '/board';              // cover page
        return '/user-admin';                                                  // own profile
    }
}
